import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { DeleteOutline, EmailOutlined, PhoneOutlined, SecurityOutlined, Star, StarOutline } from "@mui/icons-material";
import VerificationDialog from "./VerificationDialog";
import Button from "../common/Button";
import {
  TwoFactorUserDetailForPrimary,
  User2faDTO,
  fetchAllUserTwoFactorList,
  inactivate2FAoption,
  updateUser2faPrimary,
  useTwoFactorReducer,
  clearUser2FaListInfo,
} from "../../redux/slice/2FA/twoFactorSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import OptionsPopup from "../common/OptionsPopup";
import CustomTooltip from "../common/CustomTooltip";
import InfoPopup from "../common/InfoPopup";
import { useModalStyles } from "../common/useModalStyles";

export type accountProfile = {
  account: string | undefined;
};

const MyAccount = () => {
  const { tokens: mTokens, isDark, primaryBtnSx } = useModalStyles();

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const { User2FaListInfo } = useTwoFactorReducer();
  const { inactivateOptionStatus } = useTwoFactorReducer();
  const [confirmDeleteOptionPopup, setConfirmDeleteOptionPopup] = useState(false);
  const [deletingOption, setDeletingOption] = useState<User2faDTO | null>(null);
  const [deletingMethodType, setDeletingMethodType] = useState<"email" | "phone" | null>(null);

  const [confirmPrimaryPopup, setConfirmPrimaryPopup] = useState(false);
  const [selectedPrimaryOption, setSelectedPrimaryOption] = useState<User2faDTO | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    //CS-1533 : Clear any existing 2FA data first to prevent showing inactive numbers briefly
    dispatch(clearUser2FaListInfo());
    dispatch(fetchAllUserTwoFactorList(true));
  }, [dispatch]);

  const emailEvents =
    User2FaListInfo &&
    User2FaListInfo.filter((event) => event.methodType === "EMAIL");

  const phoneEvents = useMemo(() => {
    return (
      User2FaListInfo &&
      User2FaListInfo
        .filter((event) => event.methodType === "MOBILE")
        .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
    );
  }, [User2FaListInfo]);

  const disableOption = (option: User2faDTO) => {
    dispatch(inactivate2FAoption(option.user2faId));
  };

  const openOptionsPopup = (method: User2faDTO, methodType: "email" | "phone") => {
    setDeletingOption(method);
    setDeletingMethodType(methodType);
    setConfirmDeleteOptionPopup(true);
  };

  const cancelOptionsPopup = () => {
    setConfirmDeleteOptionPopup(false);
    setDeletingOption(null);
    setDeletingMethodType(null);
  };

  useEffect(() => {
    if (inactivateOptionStatus === "success")
      dispatch(fetchAllUserTwoFactorList(true));
  }, [inactivateOptionStatus]);

  const openPrimaryPopup = (option: User2faDTO) => {
    setSelectedPrimaryOption(option);
    setConfirmPrimaryPopup(true);
  };

  const setPrimaryPhone = (selectedOption: User2faDTO) => {
    let obj: TwoFactorUserDetailForPrimary = {
      user2faId: selectedOption.user2faId,
      userId: selectedOption.userId,
      methodValue: selectedOption.methodValue,
      methodType: "MOBILE",
      isPrimary: true,
    };
    dispatch(updateUser2faPrimary(obj)).then(() => {
      dispatch(fetchAllUserTwoFactorList(true));
    });
  };

  const primaryChipSx = {
    backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
    color: mTokens.accent,
    fontWeight: 700,
    border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
  };

  // Section label — deliberately lighter than the page-level header below
  // (small uppercase eyebrow + inline icon, no icon box, no bold dark text)
  // so the hierarchy reads: page title > section label > rows, instead of
  // two same-weight headers stacked on top of each other.
  const SectionLabel = ({
    icon,
    title,
    action,
  }: {
    icon: React.ReactNode;
    title: string;
    action?: React.ReactNode;
  }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} mb={1.2} flexWrap="wrap">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.7,
          color: mTokens.accent,
          "& svg": { fontSize: "1rem" },
        }}
      >
        {icon}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );

  const rowListSx = {
    display: "flex",
    flexDirection: "column" as const,
    gap: 1,
    mb: 2,
  };

  const rowSx = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    justifyContent: "space-between",
    gap: 1,
    px: 1.8,
    py: 1.2,
    borderRadius: "12px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.015)",
    transition: "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease",
    "&:hover": {
      borderColor: isDark ? "rgba(20,184,166,0.35)" : "rgba(20,184,166,0.3)",
      backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.045)",
    },
  };

  const rowIconSx = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: `${mTokens.accent}14`,
    color: mTokens.accent,
    flexShrink: 0,
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gap={1.4} mb={0.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "11px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${mTokens.accent}14`,
              color: mTokens.accent,
              flexShrink: 0,
            }}
          >
            <SecurityOutlined fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: mTokens.textPrimary, fontSize: "1.1rem", lineHeight: 1.2 }}>
              2-Factor Authentication
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.15 }}>
              Verification methods used to confirm it's you at sign-in
            </Typography>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <SectionLabel
          icon={<EmailOutlined fontSize="small" />}
          title="Emails"
          action={
            <Button
              variant="contained"
              removeUpperCase
              onClick={() => setEmailDialogOpen(true)}
              sx={{ ...primaryBtnSx, minWidth: 140 }}
            >
              Add Email
            </Button>
          }
        />
        <Box sx={rowListSx}>
          {emailEvents && emailEvents.length > 0 ? (
            emailEvents.map((event, index) => (
              <Box key={event.user2faId ?? index} sx={rowSx}>
                <Box display="flex" alignItems="center" gap={1.2} minWidth={0}>
                  <Box sx={rowIconSx}>
                    <EmailOutlined sx={{ fontSize: "1.05rem" }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: mTokens.textPrimary, fontSize: "0.9rem" }} noWrap>
                    {event.methodValue}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {event.isPrimary && <Chip label="Primary" size="small" sx={primaryChipSx} />}
                  {!event.isPrimary && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => openOptionsPopup(event, "email")}
                      size="small"
                      sx={{
                        color: mTokens.textSecondary,
                        "&:hover": { color: mTokens.error, backgroundColor: `${mTokens.error}14` },
                      }}
                    >
                      <DeleteOutline color="error" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography sx={{ fontSize: "0.86rem", color: mTokens.textSecondary }}>
                No emails added yet.
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ height: "1px", backgroundColor: mTokens.border, my: 0.5 }} />
      </Grid>

      <Grid item xs={12}>
        <SectionLabel
          icon={<PhoneOutlined fontSize="small" />}
          title="Phone Numbers"
          action={
            <Button
              variant="contained"
              removeUpperCase
              onClick={() => setPhoneDialogOpen(true)}
              sx={{ ...primaryBtnSx, minWidth: 140 }}
            >
              Add Phone
            </Button>
          }
        />
        <Box sx={rowListSx}>
          {phoneEvents && phoneEvents.length > 0 ? (
            phoneEvents.map((event, index) => (
              <Box key={event.user2faId ?? index} sx={rowSx}>
                <Box display="flex" alignItems="center" gap={1.2} minWidth={0}>
                  <Box sx={rowIconSx}>
                    <PhoneOutlined sx={{ fontSize: "1.05rem" }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: mTokens.textPrimary, fontSize: "0.9rem" }} noWrap>
                    {event.methodValue}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {event.isPrimary ? (
                    <Chip
                      label={
                        <Box flexDirection="row" display="flex" alignItems="center" gap={0.6}>
                          <span>Primary</span>
                          <Star sx={{ fontSize: "1rem" }} color="warning" />
                        </Box>
                      }
                      size="small"
                      sx={primaryChipSx}
                    />
                  ) : (
                    <>
                      <CustomTooltip title="Set as a Primary" arrow placement="bottom">
                        <IconButton
                          onClick={() => openPrimaryPopup(event)}
                          size="small"
                          sx={{
                            color: mTokens.textSecondary,
                            "&:hover": { color: "#f59e0b", backgroundColor: "rgba(245,158,11,0.12)" },
                          }}
                        >
                          <StarOutline color="warning" />
                        </IconButton>
                      </CustomTooltip>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => openOptionsPopup(event, "phone")}
                        size="small"
                        sx={{
                          color: mTokens.textSecondary,
                          "&:hover": { color: mTokens.error, backgroundColor: `${mTokens.error}14` },
                        }}
                      >
                        <DeleteOutline color="error" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography sx={{ fontSize: "0.86rem", color: mTokens.textSecondary }}>
                No phone numbers added yet.
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      {emailDialogOpen && (
        <VerificationDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          type="email"
        />
      )}
      {phoneDialogOpen && (
        <VerificationDialog
          open={phoneDialogOpen}
          onClose={() => setPhoneDialogOpen(false)}
          type="phone"
        />
      )}
      {confirmDeleteOptionPopup && (
        <OptionsPopup
          open={confirmDeleteOptionPopup}
          onClose={() => setConfirmDeleteOptionPopup(false)}
          variant="confirm"
          message={`Are you sure you want to delete this this ${deletingMethodType === "email" ? "email" : "phone number"}?`}
          buttons={[
            {
              name: "Confirm",
              color: "primary",
              onClick: () => {
                if (deletingOption) disableOption(deletingOption);
                setConfirmDeleteOptionPopup(false);
              },
            },
            { name: "Cancel", color: "secondary", onClick: cancelOptionsPopup },
          ]}
        />
      )}
      {confirmPrimaryPopup && (
        <InfoPopup
          open={confirmPrimaryPopup}
          onClose={() => setConfirmPrimaryPopup(false)}
          severity="warning"
          content={`Are you sure you want to set this phone number as your primary?`}
          confirmButton={"Confirm"}
          title="Set as Primary"
          onConfirm={() => {
            if (selectedPrimaryOption) {
              setPrimaryPhone(selectedPrimaryOption);
            }
            setConfirmPrimaryPopup(false);
            setSelectedPrimaryOption(null);
          }}
        />
      )}
    </Grid>
  );
};

export default MyAccount;
